import { pageWrap, tableHeaderCell, tableRow, btnPrimary, minutesFmt } from "../../utils/pageStyles";
// src/pages/admin/AdminAuditLogsPage.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useAppSnackbar } from "../../components/AppSnackbarProvider";
import { useTranslation } from "react-i18next";

import { AdminAuditAPI } from "../../api/adminAuditApi";

function safeJson(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

export default function AdminAuditLogsPage() {
  const { showSnackbar } = useAppSnackbar();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const [limit, setLimit] = useState(200);
  const [q, setQ] = useState("");
  const [action, setAction] = useState("");
  const [entity, setEntity] = useState("");
  const [adminId, setAdminId] = useState("");

  const [selected, setSelected] = useState(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await AdminAuditAPI.list({
        limit,
        q,
        action,
        entity,
        admin_id: adminId,
      });
      const data  = resp?.data ?? resp;   // axios wraps in .data
      const items = Array.isArray(data?.items) ? data.items : [];
      // Normalize
      const normalized = items.map((it) => ({
        id:              it.id,
        admin_id:        it.admin_id,
        admin_username:  it.admin_username  || null,
        admin_email:     it.admin_email     || null,
        action:          it.action,
        entity:          it.entity,
        entity_id:       it.entity_id,
        entity_username: it.entity_username || null,
        entity_email:    it.entity_email    || null,
        created_at:      it.created_at,
        payload:         it.payload,
      }));
      setRows(normalized);
    } catch (e) {
      showSnackbar(e?.userMessage || t("snackbar.error"), "error");
    } finally {
      setLoading(false);
    }
  }, [showSnackbar, t, limit, q, action, entity, adminId]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const columns = useMemo(
    () => [
      { field: "id", headerName: t("admin.audit.columns.id"), width: 90 },
      {
        field: "created_at",
        headerName: t("admin.audit.columns.date"),
        minWidth: 190,
        flex: 1,
        valueGetter: (p) => p.row.created_at || "",
      },
      {
        field: "admin_id",
        headerName: t("admin.audit.columns.admin"),
        width: 160,
        renderCell: (p) => (
          <Stack spacing={0}>
            <Typography variant="caption" fontWeight={700} noWrap>
              {p.row.admin_username || `#${p.row.admin_id}`}
            </Typography>
            {p.row.admin_email && (
              <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: 10 }}>
                {p.row.admin_email}
              </Typography>
            )}
          </Stack>
        ),
      },
      {
        field: "action",
        headerName: t("admin.audit.columns.action"),
        minWidth: 180,
        flex: 1,
        renderCell: (p) => (
          <Chip size="small" label={p.value} variant="outlined" />
        ),
      },
      {
        field: "entity",
        headerName: t("admin.audit.columns.entity"),
        width: 160,
        renderCell: (p) => (
          <Chip size="small" label={p.value} />
        ),
      },
      {
        field: "entity_id",
        headerName: t("admin.audit.columns.entityId"),
        width: 160,
        renderCell: (p) => p.row.entity_id ? (
          <Stack spacing={0}>
            <Typography variant="caption" fontWeight={700} noWrap>
              {p.row.entity_username ? p.row.entity_username : `#${p.row.entity_id}`}
            </Typography>
            {p.row.entity_email && (
              <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: 10 }}>
                {p.row.entity_email}
              </Typography>
            )}
          </Stack>
        ) : <Typography variant="caption" color="text.secondary">—</Typography>,
      },
      {
        field: "payload",
        headerName: t("admin.audit.columns.payload"),
        minWidth: 240,
        flex: 2,
        sortable: false,
        renderCell: (p) => {
          const v = safeJson(p.value);
          const preview =
            v === null
              ? "-"
              : typeof v === "string"
              ? v
              : JSON.stringify(v);
          return (
            <Typography variant="body2" sx={{ fontFamily: "monospace" }} noWrap>
              {preview}
            </Typography>
          );
        },
      },
      {
        field: "_actions",
        headerName: t("admin.audit.columns.actions"),
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (p) => (
          <Stack direction="row" spacing={1}>
            <Tooltip title={t("admin.audit.actions.view")}>
              <IconButton
                size="small"
                onClick={() => setSelected(p.row)}
              >
                <VisibilityIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("admin.audit.actions.copy")}>
              <IconButton
                size="small"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(JSON.stringify(p.row, null, 2));
                    showSnackbar(t("admin.audit.snack.copied"), "success");
                  } catch {
                    showSnackbar(t("snackbar.error"), "error");
                  }
                }}
              >
                <ContentCopyIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [showSnackbar, t]
  );

  const onSearch = () => fetchLogs();

  return (
    <Box sx={pageWrap}>
      <Stack spacing={1} sx={{ mb: 2 }}>
        <Typography variant="h5">{t("admin.audit.title")}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t("admin.audit.subtitle")}
        </Typography>

        <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems="center">
          <TextField
            size="small"
            label={t("admin.audit.filters.q")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("admin.audit.filters.qPlaceholder")}
            sx={{ minWidth: 280 }}
          />
          <TextField
            size="small"
            label={t("admin.audit.filters.action")}
            value={action}
            onChange={(e) => setAction(e.target.value)}
            placeholder="tag.update"
            sx={{ minWidth: 200 }}
          />
          <TextField
            size="small"
            label={t("admin.audit.filters.entity")}
            value={entity}
            onChange={(e) => setEntity(e.target.value)}
            placeholder="LabTag"
            sx={{ minWidth: 160 }}
          />
          <TextField
            size="small"
            label={t("admin.audit.filters.adminId")}
            value={adminId}
            onChange={(e) => setAdminId(e.target.value)}
            placeholder="1"
            sx={{ width: 140 }}
          />
          <TextField
            size="small"
            type="number"
            label={t("admin.audit.filters.limit")}
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value || 200))}
            sx={{ width: 140 }}
          />

          <Button variant="contained" onClick={onSearch}>
            {t("common.search")}
          </Button>
          <Tooltip title={t("common.refresh")}>
            <IconButton onClick={fetchLogs}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Divider sx={{ mb: 2 }} />

      <Box sx={{ height: 640, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          density="compact"
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50, 100, 200]}
          initialState={{
            pagination: { paginationModel: { pageSize: 50, page: 0 } },
          }}
        />
      </Box>

      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="md" fullWidth>
        <DialogTitle>{t("admin.audit.dialog.title")}</DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Box sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap" }}>
              {JSON.stringify(
                {
                  ...selected,
                  payload: safeJson(selected.payload),
                },
                null,
                2
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>{t("common.cancel")}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
